import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X } from 'lucide-react';

export type DatePreset = "today" | "yesterday" | "week" | "month" | "last_month" | "custom" | "all";

export const getPresetRange = (preset: DatePreset): { start: string; end: string } => {
  const formatLocalDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const now = new Date();
  const todayStr = formatLocalDate(now);

  if (preset === "today") {
    return { start: todayStr, end: `${todayStr} 23:59:59` };
  }
  if (preset === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const s = formatLocalDate(y);
    return { start: s, end: `${s} 23:59:59` };
  }
  if (preset === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // Sunday
    return { start: formatLocalDate(start), end: `${todayStr} 23:59:59` };
  }
  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatLocalDate(start), end: `${todayStr} 23:59:59` };
  }
  if (preset === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
    return { start: formatLocalDate(start), end: `${formatLocalDate(end)} 23:59:59` };
  }
  return { start: "", end: "" };
};

const PRESETS: { label: string; value: DatePreset }[] = [
  { label: "All Time", value: "all" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "This Week", value: "week" },
  { label: "This Month", value: "month" },
  { label: "Last Month", value: "last_month" },
  { label: "Custom", value: "custom" },
];

interface DateRangeFilterProps {
  onFilterChange: (startDate: string, endDate: string) => void;
  initialPreset?: DatePreset;
  label?: string;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ onFilterChange, initialPreset = 'all', label }) => {
  const [activePreset, setActivePreset] = useState<DatePreset>(initialPreset);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowCustomPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const applyPreset = (preset: DatePreset) => {
    setActivePreset(preset);
    setShowCustomPicker(false);
 
    if (preset === "custom") {
      setShowCustomPicker(true);
      return;
    }

    const { start, end } = getPresetRange(preset);
    onFilterChange(start, end);
  };

  const applyCustomRange = () => {
    if (!customStart || !customEnd) return;
    setShowCustomPicker(false);
    // Append the end-of-day time to include items created on the end date itself
    onFilterChange(customStart, `${customEnd} 23:59:59`);
  };

  const clearFilter = () => {
    setActivePreset("all");
    setCustomStart("");
    setCustomEnd("");
    setShowCustomPicker(false);
    onFilterChange("", "");
  };

  return (
    <div className="flex flex-col gap-1 relative z-20 w-full">
      {label && (
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </span>
      )}
      <div className="flex gap-2 items-center w-full">
        <div className="relative flex-1 w-full">
          <select
            value={activePreset}
            onChange={(e) => applyPreset(e.target.value as DatePreset)}
            className="appearance-none bg-[#151929] border-slate-700 focus:border-[#646ECD] border text-white text-sm rounded-md pl-4 pr-10 py-2 outline-none transition-colors cursor-pointer w-full h-[38px]"
          >
            {PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
            <Calendar size={14} />
          </div>
        </div>

        {activePreset !== "all" && (
          <button
            onClick={clearFilter}
            className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all duration-200 shrink-0 h-[38px]"
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>

      {showCustomPicker && (
        <div
          ref={pickerRef}
          className="absolute right-0 top-12 flex flex-col gap-3 p-4 rounded-xl bg-[#1E2437] border border-gray-700 shadow-2xl w-[280px] max-w-[calc(100vw-2rem)] z-50"
        >
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Start Date</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="bg-[#151929] border-gray-700 border text-white text-sm rounded-lg px-3 py-1.5 outline-none focus:border-[#646ECD] transition-colors"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">End Date</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="bg-[#151929] border-gray-700 border text-white text-sm rounded-lg px-3 py-1.5 outline-none focus:border-[#646ECD] transition-colors"
            />
          </div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={applyCustomRange}
              disabled={!customStart || !customEnd}
              className="flex-1 py-1.5 rounded-lg text-sm font-medium bg-[#646ECD] text-white hover:bg-[#525ab5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Apply
            </button>
            <button
              onClick={() => setShowCustomPicker(false)}
              className="flex-1 py-1.5 rounded-lg text-sm font-medium bg-[#151929] border border-gray-700 text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
